import { useEffect, useRef, useState, type RefObject } from 'react';

import { useSettingsStore } from '../settings/store.js';
import { installMatchDebugApi, removeDebugApi } from './debugApi.js';
import { Game, type GameHandlers, type GameStatus } from './Game.js';
import type { MatchSource } from './match/driver.js';
import { applyUrlSettings, parseUrlOptions } from './urlParams.js';

export type GameUiHandlers = Omit<GameHandlers, 'onStatus'>;

/** A match source plus the key that identifies it; a new key remounts the game. */
export interface GameMatch {
  source: MatchSource;
  key: string;
}

/**
 * Mounts one Game per effect run. React StrictMode runs the effect twice in development: the
 * first instance is aborted before it finishes initialising and disposed once its pending
 * renderer init settles, so nothing leaks and no loader is constructed twice.
 */
export function useGame(
  mount: RefObject<HTMLDivElement | null>,
  match: GameMatch,
  handlers: GameUiHandlers,
): { gameRef: RefObject<Game | null>; status: GameStatus } {
  const [status, setStatus] = useState<GameStatus>({ phase: 'probing' });
  const gameRef = useRef<Game | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const matchRef = useRef(match);
  matchRef.current = match;
  const key = match.key;

  useEffect(() => {
    const container = mount.current;
    if (!container) return;
    const url = parseUrlOptions(window.location.search);
    applyUrlSettings(useSettingsStore.getState(), url.settings);

    const controller = new AbortController();
    // A fresh canvas per instance: disposing a renderer loses its context for good.
    const canvas = document.createElement('canvas');
    container.append(canvas);

    const game = new Game({
      canvas,
      container,
      signal: controller.signal,
      settings: useSettingsStore.getState().settings,
      url,
      match: matchRef.current.source,
      handlers: {
        onStatus: (s) => {
          if (!controller.signal.aborted) setStatus(s);
        },
        onEscapeKey: () => handlersRef.current.onEscapeKey(),
        onPointerLockLost: () => handlersRef.current.onPointerLockLost(),
        onToggleStats: () => handlersRef.current.onToggleStats(),
        onSetupChange: (p) => handlersRef.current.onSetupChange(p),
      },
    });
    gameRef.current = game;
    installMatchDebugApi(game);

    const started = game.start().catch((err: unknown) => {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      game.fail(message);
      setStatus({ phase: 'error', message });
    });
    const unsubscribe = useSettingsStore.subscribe((state) => game.applySettings(state.settings));

    return () => {
      controller.abort();
      game.stop();
      unsubscribe();
      removeDebugApi(game);
      gameRef.current = null;
      void started.finally(() => game.dispose()).finally(() => canvas.remove());
    };
    // The key identifies the match; the source object itself changes identity on every parse.
  }, [mount, key]);

  return { gameRef, status };
}
